(() => {
  const titlePattern = /^([\u0020-\u007e]+?)\s+([^\u0000-\u007f].*)$/u;
  const japaneseFirstTitlePattern = /^([^\u0000-\u007f].*?)\s+([\u0020-\u007e]+)$/u;
  const productTitleSelector = '[data-coacer-product-title], .product__title, .product-title a';

  const updateHeader = () => {
    document.querySelectorAll('[data-coacer-header]').forEach((header) => {
      header.classList.toggle('is-scrolled', window.scrollY > 24);
    });
  };

  const updatePageTop = () => {
    const isVisible = window.scrollY > 480;
    document.querySelectorAll('[data-coacer-page-top]').forEach((button) => {
      button.classList.toggle('is-visible', isVisible);
      button.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      button.tabIndex = isVisible ? 0 : -1;
    });
  };

  let headerFrame;
  const requestHeaderUpdate = () => {
    if (headerFrame) return;
    headerFrame = window.requestAnimationFrame(() => {
      updateHeader();
      updatePageTop();
      headerFrame = undefined;
    });
  };

  const initPageTop = (scope = document) => {
    if (!document.body || document.body.dataset.coacerPageTopReady === 'true') return;

    document.addEventListener('click', (event) => {
      const button = event.target.closest?.('[data-coacer-page-top]');
      if (!button) return;

      event.preventDefault();
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
    });

    document.body.dataset.coacerPageTopReady = 'true';
  };

  const splitProductTitles = (scope = document) => {
    const titleElements = [];
    if (scope instanceof Element && scope.matches(productTitleSelector)) titleElements.push(scope);
    scope.querySelectorAll?.(productTitleSelector).forEach((element) => titleElements.push(element));

    titleElements.forEach((link) => {
      if (link.dataset.coacerTitleSplit === 'true') return;

      const title = link.textContent.trim();
      const match = title.match(titlePattern);
      const japaneseFirstMatch = match ? null : title.match(japaneseFirstTitlePattern);
      if (!match && !japaneseFirstMatch) return;

      const english = document.createElement('span');
      english.className = 'csh-product-title__english';
      english.textContent = match ? match[1] : japaneseFirstMatch[2];

      const japanese = document.createElement('span');
      japanese.className = 'csh-product-title__japanese';
      japanese.textContent = match ? match[2] : japaneseFirstMatch[1];

      if (japaneseFirstMatch) {
        link.replaceChildren(japanese, english);
        link.dataset.coacerTitleOrder = 'japanese-first';
      } else {
        link.replaceChildren(english, japanese);
        link.dataset.coacerTitleOrder = 'english-first';
      }
      link.dataset.coacerTitleSplit = 'true';
    });
  };

  const observeProductTitles = () => {
    if (!document.body || document.body.dataset.coacerTitleObserver === 'true') return;

    const titleObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) splitProductTitles(node);
        });
      });
    });

    titleObserver.observe(document.body, { childList: true, subtree: true });
    document.body.dataset.coacerTitleObserver = 'true';
  };

  const normalizeDescriptionLine = (value) => value
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const getDescriptionLines = (paragraph) => paragraph.innerHTML
    .split(/<br\b[^>]*>/gi)
    .map((fragment) => {
      const parser = document.createElement('div');
      parser.innerHTML = fragment;
      return normalizeDescriptionLine(parser.textContent || '');
    })
    .filter(Boolean)
    .flatMap((line) => line
      .split(/(?=栄養成分表示|[※＊*]\s*食生活)/u)
      .map(normalizeDescriptionLine)
      .filter(Boolean));

  const parseDescriptionDataLine = (line) => {
    const bracketMatch = line.match(/^[<＜〈]\s*([^>＞〉]+?)\s*[>＞〉]\s*(.*)$/u);
    if (bracketMatch) return { label: bracketMatch[1], value: bracketMatch[2] };

    const nutritionMatch = line.match(/^(栄養成分表示(?:\s*[（(][^）)]*[）)])?)\s*(.*)$/u);
    if (nutritionMatch) return { label: nutritionMatch[1], value: nutritionMatch[2] };

    const colonMatch = line.match(/^([^：:]{1,18})[：:]\s*(.+)$/u);
    if (colonMatch) return { label: colonMatch[1], value: colonMatch[2] };

    return null;
  };

  const createDescriptionDataList = (lines, isSummary) => {
    const parsedLines = lines.map(parseDescriptionDataLine);
    if (!parsedLines[0] || parsedLines.filter(Boolean).length < 2) return null;

    const entries = [];
    const notes = [];
    lines.forEach((line, index) => {
      const parsed = parsedLines[index];
      if (parsed) {
        entries.push(parsed);
      } else if (/^[※＊*]/u.test(line)) {
        notes.push(line);
      } else if (entries.length) {
        entries[entries.length - 1].value = normalizeDescriptionLine(`${entries[entries.length - 1].value} ${line}`);
      }
    });

    const list = document.createElement('dl');
    list.className = `csh-product-description__data${isSummary ? ' csh-product-description__data--summary' : ''}`;

    entries.forEach(({ label, value }) => {
      const row = document.createElement('div');
      row.className = 'csh-product-description__data-row';

      const term = document.createElement('dt');
      term.textContent = normalizeDescriptionLine(label);

      const description = document.createElement('dd');
      description.textContent = normalizeDescriptionLine(value);

      row.append(term, description);
      list.append(row);
    });

    const group = document.createElement('div');
    group.className = 'csh-product-description__data-group';
    group.append(list);

    notes.forEach((note) => {
      const noteElement = document.createElement('p');
      noteElement.className = 'csh-product-description__note';
      noteElement.textContent = note;
      group.append(noteElement);
    });

    return group;
  };

  const createDescriptionList = (lines) => {
    const list = document.createElement('ul');
    list.className = 'csh-product-description__list';

    lines.forEach((line) => {
      const item = document.createElement('li');
      item.textContent = line
        .replace(/^(?:[\u2460-\u2473]|\d+[.．、)]|[（(]\d+[）)])\s*/u, '')
        .trim();
      list.append(item);
    });

    return list;
  };

  const enhanceProductDescriptions = (scope = document) => {
    const contents = [];
    if (scope instanceof Element && scope.matches('.csh-product-description__content')) contents.push(scope);
    scope.querySelectorAll?.('.csh-product-description__content').forEach((content) => contents.push(content));

    contents.forEach((content) => {
      if (content.dataset.coacerDescriptionReady === 'true') return;
      if (content.dataset.coacerDescriptionClean === 'true') {
        content.dataset.coacerDescriptionReady = 'true';
        return;
      }

      const paragraphs = [...content.children].filter((element) => element.tagName === 'P');
      const firstParagraph = paragraphs[0];

      paragraphs.forEach((paragraph) => {
        const lines = getDescriptionLines(paragraph);
        if (lines.length < 2) return;

        const replacement = createDescriptionDataList(lines, paragraph === firstParagraph)
          || createDescriptionList(lines);
        paragraph.replaceWith(replacement);
      });

      const subsectionHeadings = [...content.children].filter((element) => element.tagName === 'H3');
      const descriptionId = content.closest('.csh-product-description')
        ?.querySelector('.csh-product-description__header h2')?.id || 'ProductDescription';

      subsectionHeadings.forEach((heading, index) => {
        heading.textContent = heading.textContent.replace(/^[\u2460-\u2473]\s*/u, '').trim();
        heading.id ||= `${descriptionId}-Subsection-${index + 1}`;

        const subsection = document.createElement('section');
        subsection.className = 'csh-product-description__subsection';
        subsection.setAttribute('aria-labelledby', heading.id);

        let sibling = heading.nextSibling;
        heading.before(subsection);
        subsection.append(heading);

        while (sibling) {
          if (sibling instanceof Element && (sibling.tagName === 'H2' || sibling.tagName === 'H3')) break;
          const nextSibling = sibling.nextSibling;
          subsection.append(sibling);
          sibling = nextSibling;
        }
      });

      content.dataset.coacerDescriptionReady = 'true';
    });
  };

  const openCartDrawer = (trigger) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return false;

    const closeDrawer = () => {
      drawer.classList.remove('active');
      document.body.classList.remove('overflow-hidden', 'cart_draw');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    };

    if (typeof drawer.close !== 'function') drawer.close = closeDrawer;
    if (typeof drawer.open !== 'function') {
      drawer.open = () => {
        drawer.classList.add('animate');
        window.requestAnimationFrame(() => drawer.classList.add('active'));
        document.body.classList.add('overflow-hidden', 'cart_draw');
        trigger.setAttribute('aria-expanded', 'true');
        window.setTimeout(() => drawer.querySelector('.drawer__close')?.focus(), 180);
      };
    }

    if (drawer.dataset.coacerDrawerReady !== 'true') {
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') drawer.close();
      });
      const drawerStateObserver = new MutationObserver(() => {
        trigger.setAttribute('aria-expanded', drawer.classList.contains('active') ? 'true' : 'false');
      });
      drawerStateObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      drawer.dataset.coacerDrawerReady = 'true';
    }

    drawer.open(trigger);
    trigger.setAttribute('aria-expanded', 'true');
    return true;
  };

  const initHeaderInteractions = (scope = document) => {
    scope.querySelectorAll('[data-coacer-site-header]').forEach((root) => {
      if (root.dataset.coacerHeaderReady === 'true') return;

      const header = root.querySelector('[data-coacer-header]');
      const searchTrigger = root.querySelector('[data-coacer-search-trigger]');
      const searchLayer = root.querySelector('[data-coacer-search-layer]');
      const searchInput = root.querySelector('[data-coacer-search-input]');
      const accountMenu = root.querySelector('[data-coacer-account-menu]');
      const accountTrigger = root.querySelector('[data-coacer-account-trigger]');
      const accountPanel = root.querySelector('[data-coacer-account-panel]');
      const cartTrigger = root.querySelector('[data-coacer-cart-trigger]');

      const syncPanelState = () => {
        const panelIsOpen = (searchLayer && !searchLayer.hidden) || (accountPanel && !accountPanel.hidden);
        header?.classList.toggle('has-open-panel', Boolean(panelIsOpen));
      };

      const closeSearch = (restoreFocus = false) => {
        if (!searchLayer || searchLayer.hidden) return;
        searchLayer.hidden = true;
        searchTrigger?.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('csh-panel-open');
        syncPanelState();
        if (restoreFocus) searchTrigger?.focus();
      };

      const closeAccount = (restoreFocus = false) => {
        if (!accountPanel || accountPanel.hidden) return;
        accountPanel.hidden = true;
        accountTrigger?.setAttribute('aria-expanded', 'false');
        syncPanelState();
        if (restoreFocus) accountTrigger?.focus();
      };

      const openSearch = () => {
        closeAccount();
        if (!searchLayer) return;
        searchLayer.style.setProperty('--csh-search-offset', `${header?.getBoundingClientRect().height || 70}px`);
        searchLayer.hidden = false;
        searchTrigger?.setAttribute('aria-expanded', 'true');
        document.body.classList.add('csh-panel-open');
        syncPanelState();
        window.setTimeout(() => searchInput?.focus(), 30);
      };

      const openAccount = () => {
        closeSearch();
        if (!accountPanel) return;
        accountPanel.hidden = false;
        accountTrigger?.setAttribute('aria-expanded', 'true');
        syncPanelState();
      };

      searchTrigger?.addEventListener('click', () => {
        if (searchLayer?.hidden) openSearch();
        else closeSearch(true);
      });

      root.querySelectorAll('[data-coacer-search-close]').forEach((button) => {
        button.addEventListener('click', () => closeSearch(true));
      });

      accountTrigger?.addEventListener('click', () => {
        if (accountPanel?.hidden) openAccount();
        else closeAccount(true);
      });

      cartTrigger?.addEventListener('click', (event) => {
        closeSearch();
        closeAccount();
        if (openCartDrawer(cartTrigger)) event.preventDefault();
      });

      document.addEventListener('click', (event) => {
        if (accountPanel?.hidden || accountMenu?.contains(event.target)) return;
        closeAccount();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (searchLayer && !searchLayer.hidden) closeSearch(true);
        else if (accountPanel && !accountPanel.hidden) closeAccount(true);
      });

      root.dataset.coacerHeaderReady = 'true';
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      splitProductTitles();
      observeProductTitles();
      enhanceProductDescriptions();
      initHeaderInteractions();
      initPageTop();
      updateHeader();
      updatePageTop();
    }, { once: true });
  } else {
    splitProductTitles();
    observeProductTitles();
    enhanceProductDescriptions();
    initHeaderInteractions();
    initPageTop();
    updateHeader();
    updatePageTop();
  }

  window.addEventListener('scroll', requestHeaderUpdate, { passive: true });
  document.addEventListener('shopify:section:load', (event) => {
    splitProductTitles(event.target);
    enhanceProductDescriptions(event.target);
    initHeaderInteractions(event.target);
    initPageTop(event.target);
    updateHeader();
    updatePageTop();
  });
})();
